import { useEffect, useRef } from 'react';

import { DUBBING_ERROR_STATUSES, DUBBING_STATUSES, FATAL_ERROR_STATUSES, useGetProject } from '@/entities/project';

import { Status } from '@/shared/types/Status';

import { getProjectProcessingInfo } from '../lib/getProjectProcessingInfo';

export const useProjectState = () => {
  const hasVoices = (projectVoices?: null | Record<string, null | string>) => {
    return projectVoices ? Object.values(projectVoices).some(Boolean) : false;
  };

  const { data: project } = useGetProject();

  const isProjectAlreadyWasProcessedRef = useRef(false);

  const isAutogeneratingProject = Boolean(project?.is_autogeneration);

  const areNotAllSpeakersHaveVoices = Boolean(
    project && project.voice && Object.values(project.voice).some((value) => !value),
  );

  const projectProcessingInfo = getProjectProcessingInfo({
    isAutogeneratingProject,
    isProjectAlreadyWasProcessed: isProjectAlreadyWasProcessedRef.current,
    project,
  });

  const isProjectProcessed = project && !projectProcessingInfo?.statuses.includes(project.status);
  const projectFinalProcessingStatuses = projectProcessingInfo?.finalStatuses || [];
  const projectTaskType = projectProcessingInfo?.taskType || null;
  const isMergingDone = project?.status === Status.mergingDone;

  const projectHasDubbedVideo = Boolean(project?.translated_video);

  const isProjectFatalError = project && FATAL_ERROR_STATUSES.includes(project.status);
  const isProjectDubbingError = project && DUBBING_ERROR_STATUSES.includes(project.status);
  const isProjectTranslationError = project?.status === Status.translationFailed;
  const isProjectVoiceSuggestError =
    project?.status === Status.voiceUpdateFailed || project?.status === Status.voiceSuggestFailed;

  const isProjectProcessing = isProjectFatalError || isAutogeneratingProject || !isProjectProcessed;
  const isProjectDubbing = Boolean(project && DUBBING_STATUSES.includes(project.status));
  const isProjectDubbingOrDubbingFailed =
    Boolean(project && DUBBING_ERROR_STATUSES.includes(project.status)) || isProjectDubbing;

  useEffect(() => {
    if (isProjectProcessed) {
      isProjectAlreadyWasProcessedRef.current = true;
    }
  }, [isProjectProcessed]);

  return {
    areNotAllSpeakersHaveVoices,
    isAutogeneratingProject,
    isMergingDone,
    isProjectDubbing,
    isProjectDubbingError,
    isProjectDubbingOrDubbingFailed,
    isProjectFatalError,
    isProjectProcessed,
    isProjectProcessing,
    isProjectTranslationError,
    isProjectVoiceSuggestError,
    projectFinalProcessingStatuses,
    projectHasDubbedVideo,
    projectHasVoices: hasVoices(project?.voice),
    projectTaskType,
  };
};
